import {
  ChangeDetectionStrategy,
  Component,
  computed,
  DestroyRef,
  inject,
  signal,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { ConfirmationService } from 'primeng/api';
import { EMPTY, Observable, catchError, expand, forkJoin, from, map, of, reduce } from 'rxjs';
import {
  ColumnDef,
  EmptyStateComponent,
  SettingsListHeader,
  SettingsLoadingPlaceholder,
  TfColumnDefDirective,
  TfDataTableComponent,
} from '@tourfold/libraries/shared-ui';
import {
  CustomDomain,
  CustomDomainStatusEnum,
} from '@tourfold/libraries/tf-api-client-frontend-v2';
import { Brand, BrandList, BrandsApi } from '@tourfold/libraries/tf-api-client-public-v2';
import { ResourceAccessService } from '@tourfold/shared-kernels/resource-access';
import { CustomDomainsService } from './custom-domains.service';
import { provideCustomDomainsAiPageContext } from './custom-domains-list.ai-page-context';
import { AddCustomDomainDialog } from '../add-custom-domain-dialog/add-custom-domain-dialog';
import {
  resolveSettingsLoadErrorDetails,
  SettingsInlineMessage,
  SettingsLoadError,
  type SettingsLoadErrorDetails,
} from '@tourfold/features/settings-ui';

type WizardOpen = { mode: 'closed' } | { mode: 'enter' } | { mode: 'verify'; domain: CustomDomain };

interface BrandRef {
  readonly id: string;
  readonly name: string;
  readonly logoUrl: string | null;
}

@Component({
  selector: 'tf-settings-custom-domains-list',
  imports: [
    CommonModule,
    TranslateModule,
    ButtonModule,
    ConfirmDialogModule,
    TagModule,
    TooltipModule,
    EmptyStateComponent,
    SettingsListHeader,
    SettingsLoadingPlaceholder,
    TfDataTableComponent,
    TfColumnDefDirective,
    AddCustomDomainDialog,
    SettingsInlineMessage,
    SettingsLoadError,
  ],
  templateUrl: './custom-domains-list.html',
  styleUrl: './custom-domains-list.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ConfirmationService],
})
export class CustomDomains {
  private readonly service = inject(CustomDomainsService);
  private readonly translate = inject(TranslateService);
  private readonly confirm = inject(ConfirmationService);
  private readonly brandsApi = inject(BrandsApi);
  private readonly resourceAccessService = inject(ResourceAccessService);
  private readonly destroyRef = inject(DestroyRef);

  readonly domains = signal<CustomDomain[]>([]);
  private readonly _domainToBrand = signal<Map<string, BrandRef>>(new Map());
  readonly domainToBrand = this._domainToBrand.asReadonly();
  readonly isLoading = signal(false);
  readonly hasLoadedOnce = signal(false);
  readonly initialLoadFailed = signal(false);
  readonly loadError = signal<string | null>(null);
  readonly loadErrorDetails = signal<SettingsLoadErrorDetails | null>(null);
  readonly actionError = signal<string | null>(null);
  readonly showEmptyState = computed(() => this.hasLoadedOnce() && this.domains().length === 0);
  private readonly wizard = signal<WizardOpen>({ mode: 'closed' });

  readonly wizardVisible = computed(() => this.wizard().mode !== 'closed');
  readonly wizardInitialDomain = computed<CustomDomain | null>(() => {
    const w = this.wizard();
    return w.mode === 'verify' ? w.domain : null;
  });
  readonly wizardInitialStep = computed<'enter' | 'verify'>(() =>
    this.wizard().mode === 'verify' ? 'verify' : 'enter'
  );

  readonly canCreateDomain = computed(() =>
    this.resourceAccessService.canPerform('customDomains', 'create')
  );
  readonly canUpdateDomain = computed(() =>
    this.resourceAccessService.canPerform('customDomains', 'update')
  );
  readonly canDeleteDomain = computed(() =>
    this.resourceAccessService.canPerform('customDomains', 'delete')
  );
  /**
   * `resource_access.brands` is `enabled + includes 'read'` only when the
   * tenant has the brands feature flag on AND the actor has `brands:read`.
   * If either gate fails we hide the Brand column entirely (the brand chip is
   * read-only metadata; without read access there's nothing useful to show).
   */
  readonly canReadBrands = computed(() => this.resourceAccessService.canPerform('brands', 'read'));

  readonly columns = computed<ColumnDef[]>(() => {
    const cols: ColumnDef[] = [
      {
        field: 'domain',
        title: 'SETTINGS_CUSTOM_DOMAINS.COLUMN.DOMAIN',
        type: 'custom',
        width: '30%',
      },
      {
        field: 'status',
        title: 'SETTINGS_CUSTOM_DOMAINS.COLUMN.STATUS',
        type: 'custom',
        width: '15%',
      },
    ];
    if (this.canReadBrands()) {
      cols.push({
        field: '_brand',
        title: 'SETTINGS_CUSTOM_DOMAINS.COLUMN.BRAND',
        type: 'custom',
        width: '20%',
      });
    }
    cols.push(
      {
        field: 'verifiedAt',
        title: 'SETTINGS_CUSTOM_DOMAINS.COLUMN.LAST_VERIFIED',
        type: 'custom',
        width: '15%',
      },
      {
        field: '_actions',
        title: 'SETTINGS_CUSTOM_DOMAINS.COLUMN.ACTIONS',
        type: 'custom',
        width: '20%',
      }
    );
    return cols;
  });

  constructor() {
    provideCustomDomainsAiPageContext(this.domains);
    this.loadDomains();
  }

  loadDomains(): void {
    const hadLoadedOnce = this.hasLoadedOnce();
    this.isLoading.set(true);
    this.actionError.set(null);
    forkJoin({
      domains: this.service.list(),
      domainToBrand: this.loadDomainBrandMap(),
    })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: ({ domains, domainToBrand }) => {
          this.domains.set(domains.items);
          this._domainToBrand.set(domainToBrand);
          this.isLoading.set(false);
          this.loadError.set(null);
          this.loadErrorDetails.set(null);
          this.initialLoadFailed.set(false);
          this.hasLoadedOnce.set(true);
        },
        error: (error: unknown) => {
          this.isLoading.set(false);
          const message = this.translate.instant('SETTINGS_CUSTOM_DOMAINS.ERROR.LOAD_FAILED');
          if (hadLoadedOnce) {
            this.hasLoadedOnce.set(true);
            this.actionError.set(message);
          } else {
            this.loadError.set(message);
            this.initialLoadFailed.set(true);
            void this.updateLoadErrorDetails(error);
          }
        },
      });
  }

  openAddWizard(): void {
    this.wizard.set({ mode: 'enter' });
  }

  openVerifyWizard(domain: CustomDomain): void {
    this.wizard.set({ mode: 'verify', domain });
  }

  onWizardClosed(): void {
    this.wizard.set({ mode: 'closed' });
  }

  /** Called by the wizard after Step 1 succeeds — domain has been materialised. */
  onDomainCreated(domain: CustomDomain): void {
    this.domains.update((current) => [domain, ...current]);
  }

  /** Called by the wizard after a verify call resolves (success or failure). */
  onDomainUpdated(domain: CustomDomain): void {
    this.domains.update((current) => current.map((row) => (row.id === domain.id ? domain : row)));
  }

  /** Inline action button for non-VERIFIED rows (rendered next to the Remove button). */
  inlineAction(row: CustomDomain): { label: string; handler: () => void } | null {
    if (row.status === CustomDomainStatusEnum.PendingVerification) {
      return {
        label: this.translate.instant('SETTINGS_CUSTOM_DOMAINS.ACTIONS.VERIFY'),
        handler: () => this.openVerifyWizard(row),
      };
    }
    if (
      row.status === CustomDomainStatusEnum.VerificationFailed ||
      row.status === CustomDomainStatusEnum.OwnershipLost
    ) {
      return {
        label: this.translate.instant('SETTINGS_CUSTOM_DOMAINS.ACTIONS.VERIFY_NOW'),
        handler: () => this.openVerifyWizard(row),
      };
    }
    return null;
  }

  statusSeverity(status: CustomDomainStatusEnum): 'success' | 'warn' | 'danger' {
    switch (status) {
      case CustomDomainStatusEnum.Verified:
        return 'success';
      case CustomDomainStatusEnum.PendingVerification:
        return 'warn';
      default:
        return 'danger';
    }
  }

  statusLabel(status: CustomDomainStatusEnum): string {
    switch (status) {
      case CustomDomainStatusEnum.Verified:
        return this.translate.instant('SETTINGS_CUSTOM_DOMAINS.STATUS.VERIFIED');
      case CustomDomainStatusEnum.PendingVerification:
        return this.translate.instant('SETTINGS_CUSTOM_DOMAINS.STATUS.PENDING');
      case CustomDomainStatusEnum.VerificationFailed:
        return this.translate.instant('SETTINGS_CUSTOM_DOMAINS.STATUS.FAILED');
      case CustomDomainStatusEnum.OwnershipLost:
        return this.translate.instant('SETTINGS_CUSTOM_DOMAINS.STATUS.OWNERSHIP_LOST');
      default:
        return status;
    }
  }

  brandFor(domain: string): BrandRef | undefined {
    return this.domainToBrand().get(domain);
  }

  confirmRemove(row: CustomDomain): void {
    const message = this.translate
      .instant('SETTINGS_CUSTOM_DOMAINS.REMOVE_CONFIRM_MESSAGE')
      .replace('{domain}', row.domain);
    this.confirm.confirm({
      header: this.translate.instant('SETTINGS_CUSTOM_DOMAINS.REMOVE_CONFIRM_TITLE'),
      message,
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.remove(row),
    });
  }

  private remove(row: CustomDomain): void {
    this.isLoading.set(true);
    this.service
      .remove(row.id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.domains.update((current) => current.filter((d) => d.id !== row.id));
          this._domainToBrand.update((current) => {
            const next = new Map(current);
            next.delete(row.domain);
            return next;
          });
          this.isLoading.set(false);
        },
        error: () => {
          this.isLoading.set(false);
          this.actionError.set(this.translate.instant('SETTINGS_CUSTOM_DOMAINS.ERROR.DELETE_FAILED'));
        },
      });
  }

  clearActionError(): void {
    this.actionError.set(null);
  }

  private async updateLoadErrorDetails(error: unknown): Promise<void> {
    this.loadErrorDetails.set(await resolveSettingsLoadErrorDetails(error));
  }

  private loadDomainBrandMap(): Observable<Map<string, BrandRef>> {
    if (!this.canReadBrands()) {
      return of(new Map());
    }
    return this.loadBrandsPage(0).pipe(
      expand((page) => {
        const nextPage = page.page.number + 1;
        return nextPage < page.page.totalPages ? this.loadBrandsPage(nextPage) : EMPTY;
      }),
      reduce((brands, page) => brands.concat(page.items), [] as Brand[]),
      map((brands) => this.toDomainBrandMap(brands)),
      catchError(() => {
        return of(new Map());
      })
    );
  }

  private loadBrandsPage(page: number): Observable<BrandList> {
    return from(
      this.brandsApi.listBrands({
        page,
        size: 100,
        sort: ['name:asc', 'id:asc'],
      })
    );
  }

  private toDomainBrandMap(brands: ReadonlyArray<Brand>): Map<string, BrandRef> {
    const domainBrandMap = new Map<string, BrandRef>();
    for (const brand of brands) {
      for (const link of brand.linkedCustomDomains) {
        domainBrandMap.set(link.domain, {
          id: brand.id,
          name: brand.name,
          logoUrl: brand.logoUrl ?? null,
        });
      }
    }
    return domainBrandMap;
  }
}
